import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, throwError } from 'rxjs';
import { AdminSearchService } from '@app/feature/admin/search/admin-search.service';
import { AdminSearchParamsModel } from '@app/feature/admin/search/admin-search-params.model';
import { AdminSearchResultsModel } from '@app/feature/admin/search/admin-search-results.model';
import { validateDealerId } from '@app/shared/validators/dealer-id.validator';
import { AppError } from '@app/domain/model/error';
import { FormControlUtils } from '@app/util/form-group-utils';

@Component({
  selector: 'app-admin-search',
  templateUrl: './admin-search.component.html',
  styleUrls: ['./admin-search.component.scss']
})
export class AdminSearchComponent implements OnInit, OnDestroy {

  activeColumn: number;

  private formSubmitAttempt: boolean;

  isSearchPerformed = false;

  isTablePaginationHidden: boolean;

  readonly numberOfDisplayedRows = 10;

  private queryParamsSubscription: Subscription;

  searchForm: FormGroup;

  searchResults: AdminSearchResultsModel[];

  constructor(private readonly router: Router,
              private readonly activatedRoute: ActivatedRoute,
              private readonly formBuilder: FormBuilder,
              private readonly adminSearchService: AdminSearchService) {
  }


  ngOnInit() {
    this.initForm();
    this.getPublicSearchQueryParams();
  }

  ngOnDestroy() {
    this.queryParamsSubscription.unsubscribe();
  }

  oneParamRequired(c: FormGroup) {
    const licNo: string = <string>c.get('dealerLicenseNo').value;
    const dealerName: string = <string>c.get('dealerName').value;

    if (!licNo && !dealerName) {
      return { 'oneParamRequired': true };
    }
    return null;
  }

  onSubmit() {
    FormControlUtils.markAllTouched(this.searchForm);
    this.formSubmitAttempt = true;
    if (this.searchForm.valid) {
      localStorage.setItem('searchParams', JSON.stringify(this.searchForm.value));
      this.router.navigate([], {
        queryParams: this.addParamsToUrl(this.searchForm.value)
      });
    }
  }

  startNewSearch() {
    this.activeColumn = -1;
    this.formSubmitAttempt = false;
    this.searchForm.reset();
    this.isSearchPerformed = false;
    this.deleteQueryParamsFromUrl();
  }

  private initForm() {
    this.searchForm = this.formBuilder.group({
      dealerLicenseNo: ['', validateDealerId],
      dealerName: [''],
    }, { validator: this.oneParamRequired });
  }

  private performSearch(searchParams: AdminSearchParamsModel) {
    this.adminSearchService.performSearch(searchParams).subscribe((result: AdminSearchResultsModel[]) => {
        if (result.length > 0) {
          if (result.length < this.numberOfDisplayedRows) {
            this.isTablePaginationHidden = true;
          } else {
            this.isTablePaginationHidden = false;
          }
          this.searchResults = result;
          this.isSearchPerformed = true;
        } else {
          this.isSearchPerformed = false;
          this.searchForm.setErrors({ 'noResultsFound': true });
        }
      },
      error => {
        this.deleteQueryParamsFromUrl().then(() => {
          this.isSearchPerformed = false;
          if (error.error && Array.isArray(error.error) && error.error[0].code === 'tooManyResults') {
            this.formSubmitAttempt = true;
            this.searchForm.setErrors({ 'tooManyResults': true });
            return false;
          }
        })
        if (!error.error || !Array.isArray(error.error) || error.error[0].code !== 'tooManyResults') {
          throw error;
        }
      });

    // this.isSearchPerformed = true;
  }

  private addParamsToUrl(searchParams: AdminSearchParamsModel) {
    const params = {};

    ['dealerLicenseNo', 'dealerName'].map((param) => {
      if (!searchParams[param]) {
        return;
      }

      params[param] = searchParams[param].toString().trim();
    });

    return params;
  }

  private getPublicSearchQueryParams() {
    this.queryParamsSubscription = this.activatedRoute.queryParams
      .subscribe((params: AdminSearchParamsModel) => {
        if (!Object.keys(params).length) {
          this.activeColumn = -1;
          this.formSubmitAttempt = false;
          this.searchForm.reset();
          this.isSearchPerformed = false;

        } else {
          this.performSearch(params);
        }
      });
  }

  private deleteQueryParamsFromUrl(): Promise <boolean> {
    return this.router.navigate([], {
      queryParams: {}
    });
  }

}
